import {
  DeesElement,
  customElement,
  html,
  css,
  cssManager,
  property,
  state,
  directives,
  type TemplateResult,
} from '@design.estate/dees-element';

import type { IEmail } from './sz-mta-list-view.js';

declare global {
  interface HTMLElementTagNameMap {
    'sz-mta-detail-view': SzMtaDetailView;
  }
}

export interface ISmtpLogEntry {
  timestamp: string;
  direction: 'client' | 'server' | 'system';
  command: string;
  responseCode?: number;
}

export type TSmtpDeliveryPhase =
  | 'connect'
  | 'greeting'
  | 'ehlo'
  | 'starttls'
  | 'tls_handshake'
  | 'post_tls_ehlo'
  | 'auth'
  | 'mail_from'
  | 'rcpt_to'
  | 'data_command'
  | 'message_body'
  | 'final_response'
  | 'rset'
  | 'quit'
  | 'unknown';

export type TSmtpTransactionOutcome = 'succeeded' | 'failed';

export interface ISmtpTranscriptEntry {
  timestampMs: number;
  phase: TSmtpDeliveryPhase;
  direction: 'client' | 'server' | 'system';
  text: string;
  responseCode?: number;
  transferStats?: {
    bytes: number;
    chunks: number;
    durationMs: number;
  };
}

export interface IOutboundAuthenticationCheck {
  state: 'pending' | 'completed' | 'indeterminate' | 'not-evaluated';
  overall: 'pass' | 'fail' | 'indeterminate';
  checkedAt: string;
  signedMessageSha256?: string;
  error?: string;
  identity?: {
    sourceIp: string;
    addressFamily: 'ipv4' | 'ipv6';
    heloDomain: string;
    receiverHostname: string;
    envelopeFrom: string;
    dkimDomain?: string;
    dkimSelector?: string;
  };
  dkim?: Array<{
    is_valid: boolean;
    domain: string | null;
    selector: string | null;
    status: string;
    details: string | null;
  }>;
  spf?: { result: string; domain: string; ip: string; explanation: string | null } | null;
  dmarc?: {
    passed: boolean;
    policy: string;
    domain: string;
    dkim_result: string;
    spf_result: string;
    action: string;
    details: string | null;
  } | null;
}

type TOutboundAuthenticationPresentationState =
  | IOutboundAuthenticationCheck['state']
  | 'awaiting-first-attempt'
  | 'not-recorded';

export interface ISmtpTransactionAttempt {
  id: string;
  queueItemId: string;
  queueAttempt: number;
  targetHost: string;
  targetPort: number;
  recipientDomain?: string;
  recipients: string[];
  startedAt: string;
  completedAt: string;
  durationMs: number;
  outcome: TSmtpTransactionOutcome;
  retryable: boolean;
  errorType?: string;
  smtpCode?: number;
  failurePhase?: TSmtpDeliveryPhase;
  error?: string;
  outboundAuthentication?: IOutboundAuthenticationCheck;
  transcript: ISmtpTranscriptEntry[];
}

export interface IConnectionInfo {
  sourceIp: string;
  sourceHostname: string;
  destinationIp: string;
  destinationPort: number;
  tlsVersion: string;
  tlsCipher: string;
  authenticated: boolean;
  authMethod: string;
  authUser: string;
}

export interface IAuthenticationResults {
  spf: 'pass' | 'fail' | 'softfail' | 'neutral' | 'none';
  spfDomain: string;
  dkim: 'pass' | 'fail' | 'none';
  dkimDomain: string;
  dmarc: 'pass' | 'fail' | 'none';
  dmarcPolicy: string;
}

export interface IEmailDetail extends IEmail {
  smtpTransactions?: ISmtpTransactionAttempt[];
  nextAttemptAt?: number;
  nextAttemptNumber?: number;
  attachmentCount?: number;
  to: string;
  toList: string[];
  cc?: string[];
  smtpLog: ISmtpLogEntry[];
  connectionInfo: IConnectionInfo;
  authenticationResults?: IAuthenticationResults;
  outboundAuthentication?: IOutboundAuthenticationCheck;
  rejectionReason?: string;
  bounceMessage?: string;
  headers: Record<string, string>;
  body: string;
}

@customElement('sz-mta-detail-view')
export class SzMtaDetailView extends DeesElement {
  public static demo = () => html`
    <div style="padding: 24px; max-width: 1200px;">
      <sz-mta-detail-view
        .email=${{
          id: '1',
          direction: 'outbound',
          status: 'delivered',
          from: 'noreply@serve.zone',
          to: 'user@example.com',
          toList: ['user@example.com'],
          subject: 'Welcome to serve.zone',
          timestamp: '2024-01-15 14:30:22',
          messageId: '<abc123@serve.zone>',
          size: '12.4 KB',
          smtpLog: [
            { timestamp: '14:30:20', direction: 'client', command: 'EHLO mail.serve.zone' },
            { timestamp: '14:30:20', direction: 'server', command: '250-mail.example.com Hello', responseCode: 250 },
            { timestamp: '14:30:20', direction: 'server', command: '250-STARTTLS', responseCode: 250 },
            { timestamp: '14:30:20', direction: 'server', command: '250 SIZE 52428800', responseCode: 250 },
            { timestamp: '14:30:20', direction: 'client', command: 'STARTTLS' },
            { timestamp: '14:30:21', direction: 'server', command: '220 Ready to start TLS', responseCode: 220 },
            { timestamp: '14:30:21', direction: 'client', command: 'EHLO mail.serve.zone' },
            { timestamp: '14:30:21', direction: 'server', command: '250-mail.example.com Hello', responseCode: 250 },
            { timestamp: '14:30:21', direction: 'server', command: '250-AUTH PLAIN LOGIN', responseCode: 250 },
            { timestamp: '14:30:21', direction: 'server', command: '250 SIZE 52428800', responseCode: 250 },
            { timestamp: '14:30:21', direction: 'client', command: 'AUTH PLAIN [credentials redacted]' },
            { timestamp: '14:30:21', direction: 'server', command: '235 2.7.0 Authentication successful', responseCode: 235 },
            { timestamp: '14:30:21', direction: 'client', command: 'MAIL FROM:<noreply@serve.zone>' },
            { timestamp: '14:30:21', direction: 'server', command: '250 OK', responseCode: 250 },
            { timestamp: '14:30:21', direction: 'client', command: 'RCPT TO:<user@example.com>' },
            { timestamp: '14:30:21', direction: 'server', command: '250 Accepted', responseCode: 250 },
            { timestamp: '14:30:22', direction: 'client', command: 'DATA' },
            { timestamp: '14:30:22', direction: 'server', command: '354 Enter message, ending with "." on a line by itself', responseCode: 354 },
            { timestamp: '14:30:22', direction: 'client', command: '.' },
            { timestamp: '14:30:22', direction: 'server', command: '250 OK id=1pQ2rS-0003Ab-C4', responseCode: 250 },
            { timestamp: '14:30:22', direction: 'client', command: 'QUIT' },
            { timestamp: '14:30:22', direction: 'server', command: '221 mail.example.com closing connection', responseCode: 221 },
          ],
          smtpTransactions: [
            {
              id: 'smtp-attempt-1',
              queueItemId: 'queue-1',
              queueAttempt: 1,
              targetHost: 'mx1.example.com',
              targetPort: 25,
              recipientDomain: 'example.com',
              recipients: ['user@example.com'],
              startedAt: '2024-01-15T14:25:00.000Z',
              completedAt: '2024-01-15T14:25:01.000Z',
              durationMs: 1000,
              outcome: 'failed',
              retryable: true,
              errorType: 'connection',
              failurePhase: 'greeting',
              error: 'Connection closed while reading greeting',
              transcript: [
                { timestampMs: 1705328700000, phase: 'connect', direction: 'system', text: 'Connected to mx1.example.com:25' },
                { timestampMs: 1705328701000, phase: 'greeting', direction: 'system', text: 'Connection closed while reading greeting' },
              ],
            },
            {
              id: 'smtp-attempt-2',
              queueItemId: 'queue-1',
              queueAttempt: 2,
              targetHost: 'mx2.example.com',
              targetPort: 25,
              recipientDomain: 'example.com',
              recipients: ['user@example.com'],
              startedAt: '2024-01-15T14:30:20.000Z',
              completedAt: '2024-01-15T14:30:22.000Z',
              durationMs: 2000,
              outcome: 'succeeded',
              retryable: false,
              smtpCode: 250,
              transcript: [
                { timestampMs: 1705329020000, phase: 'greeting', direction: 'server', text: '220 mx2.example.com ESMTP', responseCode: 220 },
                { timestampMs: 1705329020100, phase: 'ehlo', direction: 'client', text: 'EHLO mail.serve.zone' },
                { timestampMs: 1705329020200, phase: 'ehlo', direction: 'server', text: '250 STARTTLS', responseCode: 250 },
                { timestampMs: 1705329020300, phase: 'starttls', direction: 'client', text: 'STARTTLS' },
                { timestampMs: 1705329020400, phase: 'starttls', direction: 'server', text: '220 Ready to start TLS', responseCode: 220 },
                { timestampMs: 1705329021100, phase: 'auth', direction: 'client', text: 'AUTH PLAIN [credentials redacted]' },
                { timestampMs: 1705329022000, phase: 'final_response', direction: 'server', text: '250 OK id=1pQ2rS-0003Ab-C4', responseCode: 250 },
              ],
            },
          ],
          connectionInfo: {
            sourceIp: '10.0.1.50',
            sourceHostname: 'mail.serve.zone',
            destinationIp: '93.184.216.34',
            destinationPort: 25,
            tlsVersion: 'TLSv1.3',
            tlsCipher: 'TLS_AES_256_GCM_SHA384',
            authenticated: true,
            authMethod: 'PLAIN',
            authUser: 'noreply@serve.zone',
          },
          authenticationResults: {
            spf: 'pass',
            spfDomain: 'serve.zone',
            dkim: 'pass',
            dkimDomain: 'serve.zone',
            dmarc: 'pass',
            dmarcPolicy: 'reject',
          },
          headers: {
            'From': 'noreply@serve.zone',
            'To': 'user@example.com',
            'Subject': 'Welcome to serve.zone',
            'Date': 'Mon, 15 Jan 2024 14:30:22 +0000',
            'MIME-Version': '1.0',
            'Content-Type': 'text/html; charset=UTF-8',
          },
          body: '<html>\\n<head><title>Welcome</title></head>\\n<body>\\n  <h1>Welcome to serve.zone!</h1>\\n  <p>Your account has been created successfully.</p>\\n  <a href="https://serve.zone/dashboard">Go to Dashboard</a>\\n</body>\\n</html>',
        }}
      ></sz-mta-detail-view>
    </div>
  `;

  public static demoGroups = ['MTA'];

  @property({ type: Object })
  public accessor email: IEmailDetail | null = null;

  @state()
  private accessor expandedAttemptIds: Set<string> = new Set();

  @state()
  private accessor countdownNowMs: number = Date.now();

  private initializedEmailId: string | null = null;
  private countdownInterval: ReturnType<typeof setInterval> | undefined;

  constructor() {
    super();
    this.registerStartupFunction(() => {
      this.countdownNowMs = Date.now();
      this.synchronizeCountdownTimer();
    });
    this.registerGarbageFunction(() => this.stopCountdownTimer());
  }

  protected willUpdate(changedProperties: Map<PropertyKey, unknown>): void {
    if (!changedProperties.has('email')) return;
    const emailId = this.email?.id ?? null;
    this.countdownNowMs = Date.now();
    if (emailId === this.initializedEmailId) return;

    this.initializedEmailId = emailId;
    const latestAttempt = this.email?.smtpTransactions?.at(-1);
    this.expandedAttemptIds = new Set(latestAttempt ? [latestAttempt.id] : []);
  }

  protected updated(): void {
    this.synchronizeCountdownTimer();
  }

  public static styles = [
    cssManager.defaultStyles,
    css`
      :host {
        display: block;
      }

      .header {
        display: flex;
        align-items: center;
        gap: 16px;
        margin-bottom: 24px;
      }

      .back-link {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        font-size: 14px;
        color: ${cssManager.bdTheme('#71717a', '#a1a1aa')};
        cursor: pointer;
        transition: color 200ms ease;
      }

      .back-link:hover {
        color: ${cssManager.bdTheme('#18181b', '#fafafa')};
      }

      .email-header {
        display: flex;
        align-items: center;
        gap: 12px;
        margin-bottom: 24px;
      }

      .email-subject {
        font-size: 24px;
        font-weight: 700;
        color: ${cssManager.bdTheme('#18181b', '#fafafa')};
        margin: 0;
      }

      .badge-group {
        display: flex;
        gap: 8px;
      }

      .status-badge {
        display: inline-flex;
        align-items: center;
        padding: 4px 12px;
        border-radius: 9999px;
        font-size: 13px;
        font-weight: 500;
      }

      .status-badge.delivered {
        background: ${cssManager.bdTheme('#dcfce7', 'rgba(34, 197, 94, 0.2)')};
        color: ${cssManager.bdTheme('#16a34a', '#22c55e')};
      }
      .status-badge.accepted {
        background: ${cssManager.bdTheme('#dcfce7', 'rgba(34, 197, 94, 0.2)')};
        color: ${cssManager.bdTheme('#16a34a', '#22c55e')};
      }
      .status-badge.flagged {
        background: ${cssManager.bdTheme('#fef3c7', 'rgba(245, 158, 11, 0.2)')};
        color: ${cssManager.bdTheme('#b45309', '#fbbf24')};
      }
      .status-badge.stored {
        background: ${cssManager.bdTheme('#ccfbf1', 'rgba(20, 184, 166, 0.2)')};
        color: ${cssManager.bdTheme('#0f766e', '#2dd4bf')};
      }


      .status-badge.bounced,
      .status-badge.rejected {
        background: ${cssManager.bdTheme('#fee2e2', 'rgba(239, 68, 68, 0.2)')};
        color: ${cssManager.bdTheme('#dc2626', '#ef4444')};
      }

      .status-badge.deferred {
        background: ${cssManager.bdTheme('#fef9c3', 'rgba(250, 204, 21, 0.2)')};
        color: ${cssManager.bdTheme('#ca8a04', '#facc15')};
      }

      .status-badge.pending {
        background: ${cssManager.bdTheme('#dbeafe', 'rgba(59, 130, 246, 0.2)')};
        color: ${cssManager.bdTheme('#2563eb', '#60a5fa')};
      }

      .direction-badge {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 4px 12px;
        border-radius: 9999px;
        font-size: 13px;
        font-weight: 500;
      }

      .direction-badge.inbound {
        background: ${cssManager.bdTheme('#dcfce7', 'rgba(34, 197, 94, 0.2)')};
        color: ${cssManager.bdTheme('#16a34a', '#22c55e')};
      }

      .direction-badge.outbound {
        background: ${cssManager.bdTheme('#dbeafe', 'rgba(59, 130, 246, 0.2)')};
        color: ${cssManager.bdTheme('#2563eb', '#60a5fa')};
      }

      .content {
        display: grid;
        grid-template-columns: 1fr;
        gap: 24px;
      }

      @media (min-width: 1024px) {
        .content {
          grid-template-columns: 2fr 1fr;
        }
      }

      .main-content {
        display: flex;
        flex-direction: column;
        gap: 24px;
      }

      .sidebar {
        display: flex;
        flex-direction: column;
        gap: 24px;
      }

      .card-header {
        height: 36px;
        display: flex;
        align-items: center;
        padding: 0 16px;
        width: 100%;
        box-sizing: border-box;
      }

      .card-heading {
        flex: 1;
        display: flex;
        align-items: baseline;
        gap: 8px;
        min-width: 0;
      }

      .card-title {
        font-weight: 500;
        font-size: 13px;
        letter-spacing: -0.01em;
        color: var(--dees-color-text-secondary);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .card-subtitle {
        font-size: 12px;
        color: var(--dees-color-text-muted);
        letter-spacing: -0.01em;
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
      }

      .card-footer {
        display: flex;
        flex-direction: row;
        justify-content: flex-end;
        align-items: center;
        gap: 0;
        height: 36px;
        width: 100%;
        box-sizing: border-box;
      }

      .tile-button {
        padding: 0 16px;
        height: 100%;
        text-align: center;
        font-size: 12px;
        font-weight: 500;
        cursor: pointer;
        user-select: none;
        transition: all 0.15s ease;
        background: transparent;
        border: none;
        border-left: 1px solid var(--dees-color-border-subtle);
        color: var(--dees-color-text-muted);
        white-space: nowrap;
        display: flex;
        align-items: center;
        gap: 6px;
      }

      .tile-button:first-child {
        border-left: none;
      }

      .tile-button:hover {
        background: var(--dees-color-hover);
        color: var(--dees-color-text-primary);
      }

      .tile-button.primary {
        color: ${cssManager.bdTheme('hsl(217.2 91.2% 59.8%)', 'hsl(213.1 93.9% 67.8%)')};
        font-weight: 600;
      }

      .tile-button.primary:hover {
        background: ${cssManager.bdTheme('hsl(217.2 91.2% 59.8% / 0.08)', 'hsl(213.1 93.9% 67.8% / 0.08)')};
        color: ${cssManager.bdTheme('hsl(217.2 91.2% 50%)', 'hsl(213.1 93.9% 75%)')};
      }

      .card-content {
        padding: 16px;
      }

      .detail-list {
        display: flex;
        flex-direction: column;
        gap: 12px;
      }

      .detail-item {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
      }

      .detail-label {
        font-size: 14px;
        color: ${cssManager.bdTheme('#71717a', '#a1a1aa')};
        flex-shrink: 0;
      }

      .detail-value {
        font-size: 14px;
        color: ${cssManager.bdTheme('#18181b', '#fafafa')};
        text-align: right;
        word-break: break-all;
      }

      .smtp-log-container {
        padding: 16px;
        font-family: 'SF Mono', Monaco, 'Cascadia Code', monospace;
        font-size: 13px;
        line-height: 1.6;
        max-height: min(65vh, 720px);
        overflow-y: auto;
        overscroll-behavior: contain;
        background: ${cssManager.bdTheme('#fafafa', '#0a0a0a')};
        display: flex;
        flex-direction: column;
        gap: 8px;
        scrollbar-width: thin;
        scrollbar-color: ${cssManager.bdTheme('#d4d4d8', '#3f3f46')} transparent;
      }

      .smtp-log-container > * {
        flex-shrink: 0;
      }

      .smtp-log-container::-webkit-scrollbar {
        width: 6px;
      }

      .smtp-log-container::-webkit-scrollbar-track {
        background: transparent;
      }

      .smtp-log-container::-webkit-scrollbar-thumb {
        background: ${cssManager.bdTheme('#d4d4d8', '#3f3f46')};
        border-radius: 3px;
      }

      .smtp-attempt {
        border: 1px solid var(--dees-color-border-subtle);
        border-radius: 8px;
        overflow: hidden;
        background: ${cssManager.bdTheme('#ffffff', '#111113')};
      }

      .smtp-attempt-header {
        display: flex;
        width: 100%;
        align-items: flex-start;
        justify-content: space-between;
        gap: 16px;
        padding: 12px 14px;
        border: 0;
        background: ${cssManager.bdTheme('#f4f4f5', '#18181b')};
        color: inherit;
        font: inherit;
        text-align: left;
        cursor: pointer;
      }

      .smtp-attempt-header[aria-expanded='true'] {
        border-bottom: 1px solid var(--dees-color-border-subtle);
      }

      .smtp-attempt-header:hover {
        background: ${cssManager.bdTheme('#e4e4e7', '#27272a')};
      }

      .smtp-attempt-header:focus-visible {
        outline: 2px solid ${cssManager.bdTheme('#2563eb', '#60a5fa')};
        outline-offset: -2px;
      }

      .smtp-attempt-heading {
        min-width: 0;
      }

      .smtp-attempt-title-row {
        display: flex;
        align-items: center;
        gap: 6px;
      }

      .smtp-attempt-chevron {
        width: 14px;
        height: 14px;
        flex: 0 0 auto;
        transition: transform 150ms ease;
      }

      .smtp-attempt-header[aria-expanded='true'] .smtp-attempt-chevron {
        transform: rotate(90deg);
      }

      .smtp-attempt-title {
        color: var(--dees-color-text-primary);
        font-size: 12px;
        font-weight: 700;
      }

      .smtp-attempt-target {
        margin-top: 2px;
        color: var(--dees-color-text-muted);
        font-size: 11px;
        word-break: break-all;
      }

      .smtp-attempt-meta {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        flex-wrap: wrap;
        gap: 6px;
        color: var(--dees-color-text-muted);
        font-size: 10px;
      }

      .smtp-attempt-badge {
        display: inline-flex;
        align-items: center;
        border-radius: 9999px;
        padding: 2px 8px;
        font-size: 10px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 0.3px;
      }

      .smtp-attempt-badge.succeeded {
        background: ${cssManager.bdTheme('rgba(34, 197, 94, 0.15)', 'rgba(34, 197, 94, 0.25)')};
        color: ${cssManager.bdTheme('#16a34a', '#22c55e')};
      }

      .smtp-attempt-badge.failed.retryable,
      .smtp-attempt-badge.pending {
        background: ${cssManager.bdTheme('rgba(250, 204, 21, 0.15)', 'rgba(250, 204, 21, 0.25)')};
        color: ${cssManager.bdTheme('#a16207', '#facc15')};
      }

      .smtp-attempt-badge.failed.terminal {
        background: ${cssManager.bdTheme('rgba(239, 68, 68, 0.15)', 'rgba(239, 68, 68, 0.25)')};
        color: ${cssManager.bdTheme('#dc2626', '#ef4444')};
      }

      .smtp-attempt-transcript {
        display: flex;
        flex-direction: column;
        gap: 8px;
        padding: 10px 12px 12px;
      }

      .smtp-attempt-error {
        margin: 0 12px 12px;
        padding: 8px 10px;
        border-radius: 6px;
        background: ${cssManager.bdTheme('rgba(239, 68, 68, 0.08)', 'rgba(239, 68, 68, 0.12)')};
        color: ${cssManager.bdTheme('#991b1b', '#fca5a5')};
        font-size: 11px;
        word-break: break-word;
      }

      .smtp-attempt-content[hidden] {
        display: none;
      }

      .smtp-pending-attempt-header {
        cursor: default;
      }

      .smtp-pending-attempt-header:hover {
        background: ${cssManager.bdTheme('#f4f4f5', '#18181b')};
      }

      .smtp-pending-attempt-content {
        display: flex;
        flex-wrap: wrap;
        align-items: baseline;
        gap: 6px;
        padding: 12px 14px;
        color: var(--dees-color-text-primary);
        font-size: 12px;
      }

      .smtp-pending-attempt-time {
        color: var(--dees-color-text-muted);
      }

      .smtp-transfer-summary {
        display: flex;
        flex-direction: column;
        gap: 10px;
        padding: 12px 14px;
        border: 1px solid var(--dees-color-border-subtle);
        border-radius: 8px;
        background: ${cssManager.bdTheme('rgba(59, 130, 246, 0.05)', 'rgba(59, 130, 246, 0.09)')};
      }

      .smtp-transfer-summary-title {
        color: var(--dees-color-text-primary);
        font-size: 12px;
        font-weight: 700;
      }

      .smtp-transfer-summary-metrics {
        display: flex;
        flex-wrap: wrap;
        gap: 6px 12px;
        color: var(--dees-color-text-muted);
        font-size: 11px;
      }

      .smtp-transfer-summary-empty {
        color: var(--dees-color-text-muted);
        font-size: 11px;
      }

      .smtp-transfer-body-link {
        align-self: flex-start;
        padding: 0;
        border: 0;
        background: transparent;
        color: ${cssManager.bdTheme('#2563eb', '#60a5fa')};
        font: inherit;
        font-size: 11px;
        font-weight: 600;
        cursor: pointer;
      }

      .smtp-transfer-body-link:hover {
        text-decoration: underline;
      }

      .smtp-transfer-body-link:focus-visible {
        outline: 2px solid ${cssManager.bdTheme('#2563eb', '#60a5fa')};
        outline-offset: 2px;
      }

      .smtp-empty {
        padding: 32px 16px;
        text-align: center;
        color: var(--dees-color-text-muted);
        font-family: inherit;
        font-size: 12px;
      }

      /* Phase separators */
      .smtp-phase-separator {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 4px 0;
        margin: 4px 0;
      }

      .smtp-phase-line {
        flex: 1;
        height: 1px;
        background: ${cssManager.bdTheme('#e4e4e7', '#27272a')};
      }

      .smtp-phase-label {
        font-size: 10px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        color: ${cssManager.bdTheme('#a1a1aa', '#52525b')};
        white-space: nowrap;
      }

      /* Chat bubbles */
      .smtp-bubble {
        border-radius: 8px;
        padding: 10px 14px;
        max-width: 70%;
      }

      .smtp-bubble.client {
        background: ${cssManager.bdTheme('rgba(59, 130, 246, 0.08)', 'rgba(59, 130, 246, 0.12)')};
      }

      .smtp-bubble.server {
        background: ${cssManager.bdTheme('rgba(34, 197, 94, 0.06)', 'rgba(34, 197, 94, 0.10)')};
      }

      .smtp-bubble.theirs {
        align-self: flex-start;
        margin-right: auto;
        border-left: 3px solid ${cssManager.bdTheme('#3b82f6', '#60a5fa')};
      }

      .smtp-bubble.theirs.server {
        border-left-color: ${cssManager.bdTheme('#22c55e', '#4ade80')};
      }

      .smtp-bubble.ours {
        align-self: flex-end;
        border-right: 3px solid ${cssManager.bdTheme('#22c55e', '#4ade80')};
        margin-left: auto;
        text-align: right;
      }

      .smtp-bubble.ours.client {
        border-right-color: ${cssManager.bdTheme('#3b82f6', '#60a5fa')};
      }

      .smtp-bubble.system {
        align-self: stretch;
        max-width: none;
        background: ${cssManager.bdTheme('rgba(113, 113, 122, 0.08)', 'rgba(161, 161, 170, 0.1)')};
        border-left: 3px solid ${cssManager.bdTheme('#a1a1aa', '#71717a')};
      }

      .smtp-bubble-command {
        white-space: pre-wrap;
        word-break: break-all;
        color: ${cssManager.bdTheme('#18181b', '#fafafa')};
      }

      .smtp-bubble-meta {
        display: flex;
        align-items: center;
        gap: 6px;
        margin-top: 4px;
        font-size: 11px;
        color: ${cssManager.bdTheme('#a1a1aa', '#52525b')};
      }

      .smtp-bubble.ours .smtp-bubble-meta {
        justify-content: flex-end;
      }

      .smtp-direction-tag {
        font-size: 10px;
        font-weight: 600;
      }

      .smtp-direction-tag.client {
        color: ${cssManager.bdTheme('#3b82f6', '#60a5fa')};
      }

      .smtp-direction-tag.server {
        color: ${cssManager.bdTheme('#22c55e', '#4ade80')};
      }

      .smtp-direction-tag.system {
        color: ${cssManager.bdTheme('#71717a', '#a1a1aa')};
      }

      /* Response code badges */
      .response-code-badge {
        display: inline-block;
        padding: 1px 7px;
        border-radius: 9999px;
        font-size: 11px;
        font-weight: 700;
        margin-bottom: 4px;
        font-family: 'SF Mono', Monaco, 'Cascadia Code', monospace;
      }

      .response-code-badge.code-2xx {
        background: ${cssManager.bdTheme('rgba(34, 197, 94, 0.15)', 'rgba(34, 197, 94, 0.25)')};
        color: ${cssManager.bdTheme('#16a34a', '#22c55e')};
      }

      .response-code-badge.code-3xx {
        background: ${cssManager.bdTheme('rgba(59, 130, 246, 0.15)', 'rgba(59, 130, 246, 0.25)')};
        color: ${cssManager.bdTheme('#2563eb', '#60a5fa')};
      }

      .response-code-badge.code-4xx {
        background: ${cssManager.bdTheme('rgba(250, 204, 21, 0.15)', 'rgba(250, 204, 21, 0.25)')};
        color: ${cssManager.bdTheme('#ca8a04', '#facc15')};
      }

      .response-code-badge.code-5xx {
        background: ${cssManager.bdTheme('rgba(239, 68, 68, 0.15)', 'rgba(239, 68, 68, 0.25)')};
        color: ${cssManager.bdTheme('#dc2626', '#ef4444')};
      }

      /* SMTP metadata banner — sits inside content, above the log */
      .smtp-header-subtitle {
        padding: 10px 16px;
        font-size: 12px;
        color: var(--dees-color-text-muted);
        border-bottom: 1px solid var(--dees-color-border-subtle);
        font-family: monospace;
      }

      .smtp-direction-badge {
        display: inline-flex;
        align-items: center;
        padding: 2px 8px;
        border-radius: 9999px;
        font-size: 11px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.3px;
      }

      .smtp-direction-badge.inbound {
        background: ${cssManager.bdTheme('rgba(34, 197, 94, 0.12)', 'rgba(34, 197, 94, 0.2)')};
        color: ${cssManager.bdTheme('#16a34a', '#22c55e')};
      }

      .smtp-direction-badge.outbound {
        background: ${cssManager.bdTheme('rgba(59, 130, 246, 0.12)', 'rgba(59, 130, 246, 0.2)')};
        color: ${cssManager.bdTheme('#2563eb', '#60a5fa')};
      }

      .email-body-container {
        padding: 16px;
        font-family: monospace;
        font-size: 13px;
        max-height: min(65vh, 720px);
        overflow-y: auto;
        overscroll-behavior: contain;
        background: ${cssManager.bdTheme('#fafafa', '#0a0a0a')};
        white-space: pre-wrap;
        word-break: break-all;
        color: ${cssManager.bdTheme('#18181b', '#fafafa')};
      }

      .tls-badge {
        display: inline-flex;
        align-items: center;
        padding: 2px 8px;
        border-radius: 4px;
        font-size: 12px;
        font-weight: 500;
        background: ${cssManager.bdTheme('#dcfce7', 'rgba(34, 197, 94, 0.2)')};
        color: ${cssManager.bdTheme('#16a34a', '#22c55e')};
      }

      .auth-row {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 8px 0;
        border-bottom: 1px solid ${cssManager.bdTheme('#f4f4f5', '#27272a')};
      }

      .auth-row:last-child {
        border-bottom: none;
      }

      .auth-label {
        font-size: 14px;
        font-weight: 500;
        color: ${cssManager.bdTheme('#18181b', '#fafafa')};
      }

      .auth-domain {
        font-size: 12px;
        color: ${cssManager.bdTheme('#71717a', '#a1a1aa')};
        margin-left: 8px;
      }

      .auth-badge {
        display: inline-flex;
        align-items: center;
        padding: 2px 8px;
        border-radius: 9999px;
        font-size: 12px;
        font-weight: 500;
      }

      .auth-badge.pass {
        background: ${cssManager.bdTheme('#dcfce7', 'rgba(34, 197, 94, 0.2)')};
        color: ${cssManager.bdTheme('#16a34a', '#22c55e')};
      }

      .auth-badge.fail {
        background: ${cssManager.bdTheme('#fee2e2', 'rgba(239, 68, 68, 0.2)')};
        color: ${cssManager.bdTheme('#dc2626', '#ef4444')};
      }

      .auth-badge.softfail,
      .auth-badge.neutral,
      .auth-badge.none {
        background: ${cssManager.bdTheme('#fef9c3', 'rgba(250, 204, 21, 0.2)')};
        color: ${cssManager.bdTheme('#ca8a04', '#facc15')};
      }

      dees-tile.rejection-card::part(outer) {
        border-color: ${cssManager.bdTheme('#fecaca', 'rgba(239, 68, 68, 0.3)')};
      }

      .rejection-content {
        font-size: 14px;
        color: ${cssManager.bdTheme('#dc2626', '#ef4444')};
      }

      .rejection-label {
        font-size: 12px;
        font-weight: 600;
        text-transform: uppercase;
        letter-spacing: 0.05em;
        color: ${cssManager.bdTheme('#71717a', '#a1a1aa')};
        margin-bottom: 4px;
      }

      .rejection-text {
        font-family: monospace;
        font-size: 13px;
        padding: 8px 12px;
        background: ${cssManager.bdTheme('#fef2f2', 'rgba(239, 68, 68, 0.1)')};
        border-radius: 4px;
        margin-bottom: 12px;
        color: ${cssManager.bdTheme('#991b1b', '#fca5a5')};
      }

      .rejection-text:last-child {
        margin-bottom: 0;
      }

      .no-email {
        padding: 48px 24px;
        text-align: center;
        color: ${cssManager.bdTheme('#71717a', '#a1a1aa')};
      }
    `,
  ];

  public render(): TemplateResult {
    if (!this.email) {
      return html`<div class="no-email">No email selected</div>`;
    }

    const email = this.email;
    const authenticationResults = email.authenticationResults || {
      spf: 'none' as const,
      spfDomain: '',
      dkim: 'none' as const,
      dkimDomain: '',
      dmarc: 'none' as const,
      dmarcPolicy: '',
    };
    const isOutbound = email.direction === 'outbound';
    const outboundAuthentication = isOutbound ? email.outboundAuthentication : undefined;
    const outboundPresentationState: TOutboundAuthenticationPresentationState | undefined =
      !isOutbound
        ? undefined
        : outboundAuthentication?.state
          || (
            email.status === 'pending'
            && (email.smtpTransactions?.length || 0) === 0
              ? 'awaiting-first-attempt'
              : 'not-recorded'
          );
    const outboundBadgeClass = outboundPresentationState === 'completed'
      ? outboundAuthentication!.overall === 'pass' ? 'pass' : 'fail'
      : 'none';
    const outboundSummary = outboundPresentationState === 'completed'
      ? outboundAuthentication!.overall
      : outboundPresentationState === 'pending'
        ? 'in progress'
        : outboundPresentationState === 'not-evaluated'
          ? 'not evaluated'
          : outboundPresentationState === 'awaiting-first-attempt'
            ? 'waiting'
            : outboundPresentationState === 'not-recorded'
              ? 'not recorded'
              : outboundPresentationState;
    const outboundSource = outboundAuthentication?.identity?.sourceIp
      || (
        outboundPresentationState === 'pending'
          ? 'awaiting source evidence'
          : outboundPresentationState === 'awaiting-first-attempt'
            ? 'awaiting first attempt'
            : 'not recorded'
      );
    const outboundEhlo = outboundAuthentication?.identity?.heloDomain
      || (
        outboundPresentationState === 'pending'
          ? 'awaiting source evidence'
          : outboundPresentationState === 'awaiting-first-attempt'
            ? 'awaiting first attempt'
            : 'not recorded'
      );
    const outboundDetail = outboundAuthentication?.error
      || (
        outboundPresentationState === 'awaiting-first-attempt'
          ? 'The self-check starts with the first outbound delivery attempt.'
          : outboundPresentationState === 'not-recorded'
            ? 'No outbound authentication self-check was recorded for this delivery. It may predate self-check support or have ended before egress evidence was available.'
            : outboundPresentationState === 'not-evaluated'
              ? 'The self-check was recorded but not evaluated.'
              : outboundPresentationState === 'indeterminate'
                ? 'The self-check ended without enough evidence for a trustworthy result.'
                : undefined
      );
    const outboundSpfResult = outboundAuthentication?.state === 'completed'
      ? outboundAuthentication.spf?.result || 'none'
      : undefined;
    const outboundSpfBadgeClass = outboundSpfResult === 'pass'
      ? 'pass'
      : outboundSpfResult === 'fail' || outboundSpfResult === 'softfail'
        ? 'fail'
        : 'none';
    const outboundDkimResult = outboundAuthentication?.state === 'completed'
      ? outboundAuthentication.dkim?.length
        ? outboundAuthentication.dkim.some((result) => result.is_valid) ? 'pass' : 'fail'
        : 'none'
      : undefined;
    const outboundDmarcResult = outboundAuthentication?.state === 'completed'
      ? outboundAuthentication.dmarc
        ? outboundAuthentication.dmarc.passed ? 'pass' : 'fail'
        : 'none'
      : undefined;

    return html`
      <div class="header">
        <div class="back-link" @click=${() => this.handleBack()}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <polyline points="15 18 9 12 15 6"></polyline>
          </svg>
          Back to Emails
        </div>
      </div>

      <div class="email-header">
        <h1 class="email-subject">${email.subject}</h1>
        <div class="badge-group">
          <span class="status-badge ${email.status}">${email.status}</span>
          <span class="direction-badge ${email.direction}">${email.direction}</span>
        </div>
      </div>

      <div class="content">
        <div class="main-content">
          <!-- Email Metadata -->
          <dees-tile>
            <div slot="header" class="card-header">
              <div class="card-heading">
                <span class="card-title">Email Metadata</span>
              </div>
            </div>
            <div class="card-content">
              <div class="detail-list">
                <div class="detail-item">
                  <span class="detail-label">From</span>
                  <span class="detail-value">${email.from}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">To</span>
                  <span class="detail-value">${email.toList.join(', ')}</span>
                </div>
                ${email.cc && email.cc.length > 0 ? html`
                  <div class="detail-item">
                    <span class="detail-label">CC</span>
                    <span class="detail-value">${email.cc.join(', ')}</span>
                  </div>
                ` : ''}
                <div class="detail-item">
                  <span class="detail-label">Subject</span>
                  <span class="detail-value">${email.subject}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Date</span>
                  <span class="detail-value">${email.timestamp}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Message ID</span>
                  <span class="detail-value">${email.messageId}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Size</span>
                  <span class="detail-value">${email.size}</span>
                </div>
              </div>
            </div>
          </dees-tile>

          <!-- SMTP Transaction Log -->
          <dees-tile>
            <div slot="header" class="card-header">
              <div class="card-heading">
                <span class="card-title">SMTP Transaction Log</span>
                <span class="smtp-direction-badge ${email.direction}">${email.direction}</span>
              </div>
            </div>
            <div class="smtp-header-subtitle">
              ${this.getSmtpHeaderSubtitle(email)}
            </div>
            ${this.renderSmtpLog(email)}
            <div slot="footer" class="card-footer">
              <button class="tile-button" @click=${() => this.copySmtpLog()}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                  <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                </svg>
                Copy Log
              </button>
            </div>
          </dees-tile>

          <!-- Email Body -->
          <dees-tile id="email-body-panel">
            <div slot="header" class="card-header">
              <div class="card-heading">
                <span class="card-title">Email Body (Escaped)</span>
                <span class="card-subtitle">Raw content — HTML is not rendered</span>
              </div>
            </div>
            <pre class="email-body-container">${email.body}</pre>
          </dees-tile>
        </div>

        <div class="sidebar">
          <!-- Connection Info -->
          <dees-tile>
            <div slot="header" class="card-header">
              <div class="card-heading">
                <span class="card-title">Connection Info</span>
              </div>
            </div>
            <div class="card-content">
              <div class="detail-list">
                <div class="detail-item">
                  <span class="detail-label">Source IP</span>
                  <span class="detail-value">${email.connectionInfo.sourceIp}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Source Hostname</span>
                  <span class="detail-value">${email.connectionInfo.sourceHostname}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">Destination</span>
                  <span class="detail-value">${email.connectionInfo.destinationIp}:${email.connectionInfo.destinationPort}</span>
                </div>
                <div class="detail-item">
                  <span class="detail-label">TLS</span>
                  <span class="detail-value">
                    ${email.connectionInfo.tlsVersion
                      ? html`<span class="tls-badge">${email.connectionInfo.tlsVersion}</span>`
                      : 'None'}
                  </span>
                </div>
                ${email.connectionInfo.tlsCipher ? html`
                  <div class="detail-item">
                    <span class="detail-label">Cipher</span>
                    <span class="detail-value">${email.connectionInfo.tlsCipher}</span>
                  </div>
                ` : ''}
                <div class="detail-item">
                  <span class="detail-label">Authenticated</span>
                  <span class="detail-value">${email.connectionInfo.authenticated ? 'Yes' : 'No'}</span>
                </div>
                ${email.connectionInfo.authenticated ? html`
                  <div class="detail-item">
                    <span class="detail-label">Auth Method</span>
                    <span class="detail-value">${email.connectionInfo.authMethod}</span>
                  </div>
                  <div class="detail-item">
                    <span class="detail-label">Auth User</span>
                    <span class="detail-value">${email.connectionInfo.authUser}</span>
                  </div>
                ` : ''}
              </div>
            </div>
          </dees-tile>

          <!-- Authentication Results -->
          ${(email.direction === 'inbound' || isOutbound) ? html`<dees-tile data-authentication-results>
            <div slot="header" class="card-header">
              <div class="card-heading">
                <span class="card-title">${isOutbound ? 'Outbound Authentication Self-Check' : 'Authentication Results'}</span>
              </div>
            </div>
            <div class="card-content">
              ${isOutbound ? html`
                <div class="auth-row">
                  <div>
                    <span class="auth-label">Self-check</span>
                    <span class="auth-domain">Exact signed message and actual outbound source</span>
                  </div>
                  <span class="auth-badge ${outboundBadgeClass}">${outboundSummary}</span>
                </div>
                <div class="auth-row">
                  <div>
                    <span class="auth-label">Actual source</span>
                    <span class="auth-domain">${outboundSource}</span>
                  </div>
                  <span class="auth-domain">EHLO ${outboundEhlo}</span>
                </div>
                ${outboundDetail ? html`
                  <div class="auth-row">
                    <div>
                      <span class="auth-label">Result detail</span>
                      <span class="auth-domain">${outboundDetail}</span>
                    </div>
                  </div>
                ` : ''}
                <div class="auth-row">
                  <span class="auth-domain">This predicts what a receiver should see; it is not a receipt from the destination.</span>
                </div>
              ` : ''}
              ${outboundAuthentication?.state === 'completed' ? html`
                <div class="auth-row">
                  <div>
                    <span class="auth-label">SPF</span>
                    <span class="auth-domain">${outboundAuthentication.spf?.domain || 'not evaluated'}</span>
                  </div>
                  <span class="auth-badge ${outboundSpfBadgeClass}">${outboundSpfResult}</span>
                </div>
                <div class="auth-row">
                  <div>
                    <span class="auth-label">DKIM</span>
                    <span class="auth-domain">${outboundAuthentication.dkim?.map((result) => result.domain).filter(Boolean).join(', ') || 'not evaluated'}</span>
                  </div>
                  <span class="auth-badge ${outboundDkimResult}">${outboundDkimResult}</span>
                </div>
                <div class="auth-row">
                  <div>
                    <span class="auth-label">DMARC</span>
                    <span class="auth-domain">policy: ${outboundAuthentication.dmarc?.policy || 'not evaluated'}</span>
                  </div>
                  <span class="auth-badge ${outboundDmarcResult}">${outboundDmarcResult}</span>
                </div>
              ` : outboundPresentationState === 'pending' ? html`
                <div class="auth-row">
                  <span class="auth-domain">SPF, DKIM, and DMARC evaluation is in progress.</span>
                </div>
              ` : outboundPresentationState === 'awaiting-first-attempt' ? html`
                <div class="auth-row">
                  <span class="auth-domain">SPF, DKIM, and DMARC will be evaluated after the first outbound delivery attempt.</span>
                </div>
              ` : isOutbound ? html`
                <div class="auth-row">
                  <span class="auth-domain">No trustworthy SPF, DKIM, and DMARC verdict was recorded for this delivery.</span>
                </div>
              ` : html`
                <div class="auth-row">
                  <div>
                    <span class="auth-label">SPF</span>
                    <span class="auth-domain">${authenticationResults.spfDomain}</span>
                  </div>
                  <span class="auth-badge ${authenticationResults.spf}">${authenticationResults.spf}</span>
                </div>
                <div class="auth-row">
                  <div>
                    <span class="auth-label">DKIM</span>
                    <span class="auth-domain">${authenticationResults.dkimDomain}</span>
                  </div>
                  <span class="auth-badge ${authenticationResults.dkim}">${authenticationResults.dkim}</span>
                </div>
                <div class="auth-row">
                  <div>
                    <span class="auth-label">DMARC</span>
                    <span class="auth-domain">policy: ${authenticationResults.dmarcPolicy}</span>
                  </div>
                  <span class="auth-badge ${authenticationResults.dmarc}">${authenticationResults.dmarc}</span>
                </div>
              `}
            </div>
          </dees-tile>` : ''}

          <!-- Rejection Details (conditional) -->
          ${email.status === 'rejected' || email.status === 'bounced' ? html`
            <dees-tile class="rejection-card">
              <div slot="header" class="card-header">
                <div class="card-heading">
                  <span class="card-title">Rejection Details</span>
                </div>
              </div>
              <div class="card-content">
                ${email.rejectionReason ? html`
                  <div class="rejection-label">Rejection Reason</div>
                  <div class="rejection-text">${email.rejectionReason}</div>
                ` : ''}
                ${email.bounceMessage ? html`
                  <div class="rejection-label">Bounce Message</div>
                  <div class="rejection-text">${email.bounceMessage}</div>
                ` : ''}
              </div>
            </dees-tile>
          ` : ''}
        </div>
      </div>
    `;
  }

  private getResponseCodeBadgeClass(code: number): string {
    if (code >= 500) return 'code-5xx';
    if (code >= 400) return 'code-4xx';
    if (code >= 300) return 'code-3xx';
    return 'code-2xx';
  }

  private getLegacySmtpPhases(log: ISmtpLogEntry[]): Array<{ phase: string; label: string; entries: ISmtpLogEntry[] }> {
    const phases: Array<{ phase: string; label: string; entries: ISmtpLogEntry[] }> = [];
    let currentPhase = '';
    let ehloCount = 0;

    for (const entry of log) {
      const cmd = entry.command.toUpperCase();
      let phase = currentPhase;

      if (entry.direction === 'client') {
        if (cmd.startsWith('EHLO') || cmd.startsWith('HELO')) {
          ehloCount++;
          if (ehloCount === 1) {
            phase = 'connection';
          } else {
            phase = 'post-tls';
          }
        } else if (cmd === 'STARTTLS') {
          phase = 'tls';
        } else if (cmd.startsWith('AUTH')) {
          phase = 'auth';
        } else if (cmd.startsWith('MAIL FROM') || cmd.startsWith('RCPT TO') || cmd === 'DATA' || cmd === '.') {
          phase = 'transfer';
        } else if (cmd === 'QUIT') {
          phase = 'closing';
        }
      }

      // Server responses stay in the current phase
      if (entry.direction === 'server' && phase === '') {
        phase = currentPhase || 'connection';
      }
      if (phase === '') phase = 'connection';

      if (phase !== currentPhase) {
        currentPhase = phase;
        const labels: Record<string, string> = {
          'connection': 'Connection',
          'tls': 'TLS Negotiation',
          'post-tls': 'Post-TLS Handshake',
          'auth': 'Authentication',
          'transfer': 'Mail Transfer',
          'closing': 'Closing',
        };
        phases.push({ phase, label: labels[phase] || phase, entries: [] });
      }

      if (phases.length === 0) {
        phases.push({ phase: 'connection', label: 'Connection', entries: [] });
      }
      phases[phases.length - 1].entries.push(entry);
    }

    return phases;
  }

  private getSmtpHeaderSubtitle(email: IEmailDetail): string {
    const transactions = email.smtpTransactions || [];
    if (transactions.length > 0) {
      const latest = transactions[transactions.length - 1];
      const attemptLabel = transactions.length === 1 ? 'attempt' : 'attempts';
      return `${transactions.length} ${attemptLabel} · latest ${latest.targetHost}:${latest.targetPort}`;
    }

    const source = email.direction === 'outbound'
      ? email.connectionInfo.sourceHostname
      : email.connectionInfo.sourceIp;
    const destinationHost = email.direction === 'outbound'
      ? email.connectionInfo.destinationIp
      : email.connectionInfo.sourceHostname;
    const destination = destinationHost
      ? `${destinationHost}${email.connectionInfo.destinationPort ? `:${email.connectionInfo.destinationPort}` : ''}`
      : '';
    if (!source && !destination) {
      return 'No connection metadata available';
    }
    return `${source || 'unknown'} → ${destination || 'unknown'}`;
  }

  private getSmtpPhaseLabel(phase: string): string {
    const labels: Record<string, string> = {
      connect: 'Connection',
      connection: 'Connection',
      greeting: 'Server Greeting',
      ehlo: 'EHLO',
      starttls: 'STARTTLS',
      tls: 'TLS Negotiation',
      tls_handshake: 'TLS Handshake',
      post_tls_ehlo: 'Post-TLS EHLO',
      'post-tls': 'Post-TLS Handshake',
      auth: 'Authentication',
      mail_from: 'Envelope Sender',
      rcpt_to: 'Recipients',
      data_command: 'DATA Command',
      message_body: 'Message Transfer',
      final_response: 'Final Response',
      transfer: 'Mail Transfer',
      rset: 'Reset',
      quit: 'Closing',
      closing: 'Closing',
      unknown: 'Unknown Phase',
    };
    return labels[phase] || phase.replaceAll('_', ' ');
  }

  private getStructuredSmtpPhases(
    transcript: ISmtpTranscriptEntry[],
  ): Array<{ phase: TSmtpDeliveryPhase; label: string; entries: ISmtpTranscriptEntry[] }> {
    const phases: Array<{
      phase: TSmtpDeliveryPhase;
      label: string;
      entries: ISmtpTranscriptEntry[];
    }> = [];

    for (const entry of transcript) {
      const currentPhase = phases[phases.length - 1];
      if (!currentPhase || currentPhase.phase !== entry.phase) {
        phases.push({
          phase: entry.phase,
          label: this.getSmtpPhaseLabel(entry.phase),
          entries: [],
        });
      }
      phases[phases.length - 1].entries.push(entry);
    }

    return phases;
  }

  private formatSmtpTimestamp(timestampMs: number): string {
    if (!Number.isFinite(timestampMs)) return 'unknown time';
    const timestamp = new Date(timestampMs);
    return Number.isNaN(timestamp.getTime()) ? 'unknown time' : timestamp.toISOString();
  }

  private formatSmtpDuration(durationMs: number): string {
    if (!Number.isFinite(durationMs) || durationMs < 0) return 'unknown duration';
    if (durationMs < 1000) return `${Math.round(durationMs)} ms`;
    return `${(durationMs / 1000).toFixed(2)} s`;
  }

  private getSmtpIdentityClass(
    email: IEmailDetail,
    direction: ISmtpTranscriptEntry['direction'],
  ): 'ours' | 'theirs' | 'system' {
    if (direction === 'system') return 'system';
    const isOurs = email.direction === 'outbound'
      ? direction === 'client'
      : direction === 'server';
    return isOurs ? 'ours' : 'theirs';
  }

  private getSmtpDirectionLabel(
    email: IEmailDetail,
    direction: ISmtpTranscriptEntry['direction'],
    attempt?: ISmtpTransactionAttempt,
  ): string {
    if (direction === 'system') return 'System';
    const identity = this.getSmtpIdentityClass(email, direction);
    const role = direction;
    let remoteHostname = '';
    if (identity === 'theirs') {
      remoteHostname = email.direction === 'outbound'
        ? attempt?.targetHost || ''
        : email.connectionInfo.sourceHostname || '';
    }
    return `${identity === 'ours' ? 'Us' : 'Remote'} · ${role}${
      remoteHostname ? ` · ${remoteHostname}` : ''
    }`;
  }

  private redactSmtpText(
    text: string,
    direction: 'client' | 'server' | 'system',
    phase?: TSmtpDeliveryPhase,
  ): string {
    if (phase === 'message_body') return '[message body omitted]';
    if (direction === 'client' && (phase === 'auth' || /^AUTH(?:\s|$)/i.test(text))) {
      const mechanism = text.trim().split(/\s+/)[1];
      return `AUTH${mechanism ? ` ${mechanism}` : ''} [credentials redacted]`;
    }
    return text;
  }

  private renderStructuredSmtpEntry(
    email: IEmailDetail,
    attempt: ISmtpTransactionAttempt,
    entry: ISmtpTranscriptEntry,
  ): TemplateResult {
    const identityClass = this.getSmtpIdentityClass(email, entry.direction);
    return html`
      <div class="smtp-bubble ${entry.direction} ${identityClass}">
        ${entry.direction === 'server' && entry.responseCode !== undefined ? html`
          <span class="response-code-badge ${this.getResponseCodeBadgeClass(entry.responseCode)}">${entry.responseCode}</span>
        ` : ''}
        <div class="smtp-bubble-command">${this.redactSmtpText(entry.text, entry.direction, entry.phase)}</div>
        <div class="smtp-bubble-meta">
          <span>${this.formatSmtpTimestamp(entry.timestampMs)}</span>
          <span>·</span>
          <span class="smtp-direction-tag ${entry.direction}">${
            this.getSmtpDirectionLabel(email, entry.direction, attempt)
          }</span>
        </div>
      </div>
    `;
  }

  private isNonNegativeFinite(value: unknown): value is number {
    return typeof value === 'number' && Number.isFinite(value) && value >= 0;
  }

  private isNonNegativeInteger(value: unknown): value is number {
    return this.isNonNegativeFinite(value) && Number.isInteger(value);
  }

  private parseEmailSize(size: string): number | undefined {
    const match = /^\s*(\d+(?:\.\d+)?)\s*(B|KB|MB|GB|TB|KiB|MiB|GiB|TiB)\s*$/i.exec(size);
    if (!match) return undefined;
    const value = Number(match[1]);
    if (!this.isNonNegativeFinite(value)) return undefined;
    const exponentByUnit: Record<string, number> = {
      b: 0,
      kb: 1,
      kib: 1,
      mb: 2,
      mib: 2,
      gb: 3,
      gib: 3,
      tb: 4,
      tib: 4,
    };
    const exponent = exponentByUnit[match[2].toLowerCase()];
    const bytes = value * (1024 ** exponent);
    return this.isNonNegativeFinite(bytes) ? bytes : undefined;
  }

  private humanizeBytes(bytes: number): string {
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let value = bytes;
    let unitIndex = 0;
    while (value >= 1024 && unitIndex < units.length - 1) {
      value /= 1024;
      unitIndex++;
    }
    const precision = unitIndex === 0 || value >= 100 ? 0 : value >= 10 ? 1 : 2;
    return `${value.toFixed(precision)} ${units[unitIndex]}`;
  }

  private getMessageTransferMetrics(
    email: IEmailDetail,
    phaseEntries: ISmtpTranscriptEntry[],
    followingEntries: ISmtpTranscriptEntry[],
  ): {
    bytes?: number;
    chunks?: number;
    durationMs?: number;
    attachmentCount?: number;
  } {
    let bytes: number | undefined;
    let chunks: number | undefined;
    let durationMs: number | undefined;

    for (const entry of phaseEntries) {
      const stats = entry.transferStats;
      if (!stats) continue;
      if (this.isNonNegativeFinite(stats.bytes)) bytes = stats.bytes;
      if (this.isNonNegativeInteger(stats.chunks)) chunks = stats.chunks;
      if (this.isNonNegativeFinite(stats.durationMs)) durationMs = stats.durationMs;
    }

    bytes ??= this.parseEmailSize(email.size);
    if (durationMs === undefined) {
      const phaseTimestamps = phaseEntries
        .map((entry) => entry.timestampMs)
        .filter((timestamp) => this.isNonNegativeFinite(timestamp));
      const followingTimestamp = followingEntries
        .map((entry) => entry.timestampMs)
        .find((timestamp) => this.isNonNegativeFinite(timestamp));
      const startTimestamp = phaseTimestamps[0];
      const endTimestamp = followingTimestamp
        ?? (phaseTimestamps.length > 1 ? phaseTimestamps.at(-1) : undefined);
      if (
        startTimestamp !== undefined
        && endTimestamp !== undefined
        && endTimestamp >= startTimestamp
      ) {
        durationMs = endTimestamp - startTimestamp;
      }
    }

    return {
      bytes,
      chunks,
      durationMs,
      attachmentCount: this.isNonNegativeInteger(email.attachmentCount)
        ? email.attachmentCount
        : undefined,
    };
  }

  private getMessageTransferMetricLabels(
    email: IEmailDetail,
    phaseEntries: ISmtpTranscriptEntry[],
    followingEntries: ISmtpTranscriptEntry[],
  ): string[] {
    const metrics = this.getMessageTransferMetrics(email, phaseEntries, followingEntries);
    return [
      ...(metrics.bytes !== undefined ? [this.humanizeBytes(metrics.bytes)] : []),
      ...(metrics.chunks !== undefined
        ? [`${metrics.chunks} ${metrics.chunks === 1 ? 'chunk' : 'chunks'}`]
        : []),
      ...(metrics.durationMs !== undefined ? [this.formatSmtpDuration(metrics.durationMs)] : []),
      ...(metrics.attachmentCount !== undefined
        ? [`${metrics.attachmentCount} ${metrics.attachmentCount === 1 ? 'attachment' : 'attachments'}`]
        : []),
    ];
  }

  private renderMessageTransferSummary(
    email: IEmailDetail,
    phaseEntries: ISmtpTranscriptEntry[],
    followingEntries: ISmtpTranscriptEntry[],
  ): TemplateResult {
    const labels = this.getMessageTransferMetricLabels(email, phaseEntries, followingEntries);
    return html`
      <div class="smtp-transfer-summary">
        <div class="smtp-transfer-summary-title">Message transfer</div>
        ${labels.length > 0 ? html`
          <div class="smtp-transfer-summary-metrics">
            ${labels.map((label) => html`<span>${label}</span>`)}
          </div>
        ` : html`<div class="smtp-transfer-summary-empty">Message body transferred</div>`}
        <button
          type="button"
          class="smtp-transfer-body-link"
          aria-controls="email-body-panel"
          @click=${() => this.scrollEmailBodyIntoView()}
        >View body ↓</button>
      </div>
    `;
  }

  private scrollEmailBodyIntoView(): void {
    const emailBodyPanel = this.shadowRoot?.querySelector('#email-body-panel');
    if (!emailBodyPanel) return;
    const reduceMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false;
    emailBodyPanel.scrollIntoView({
      behavior: reduceMotion ? 'auto' : 'smooth',
      block: 'start',
    });
  }

  private toggleSmtpAttempt(attemptIdArg: string): void {
    const nextExpandedAttemptIds = new Set(this.expandedAttemptIds);
    if (nextExpandedAttemptIds.has(attemptIdArg)) {
      nextExpandedAttemptIds.delete(attemptIdArg);
    } else {
      nextExpandedAttemptIds.add(attemptIdArg);
    }
    this.expandedAttemptIds = nextExpandedAttemptIds;
  }

  /**
   * Position of a transaction within its queue attempt's MX-fallback sequence:
   * several transactions can share one queueAttempt when the sender walks the
   * recipient domain's MX list before deferring.
   */
  private getMxPosition(
    transactions: ISmtpTransactionAttempt[],
    attempt: ISmtpTransactionAttempt,
  ): { index: number; total: number } {
    const group = transactions.filter((tx) => tx.queueAttempt === attempt.queueAttempt);
    return {
      index: group.indexOf(attempt) + 1,
      total: group.length,
    };
  }

  private renderSmtpTransaction(
    email: IEmailDetail,
    attempt: ISmtpTransactionAttempt,
    mxPosition: { index: number; total: number },
  ): TemplateResult {
    const phases = this.getStructuredSmtpPhases(attempt.transcript || []);
    const targetLabel = `${attempt.targetHost}:${attempt.targetPort}`;
    const destinationLabel = attempt.recipientDomain
      ? `${targetLabel} for ${attempt.recipientDomain}`
      : targetLabel;
    const badgeState = attempt.outcome === 'succeeded'
      ? 'succeeded'
      : attempt.retryable ? 'failed retryable' : 'failed terminal';
    const outcomeLabel = attempt.outcome === 'succeeded'
      ? 'Succeeded'
      : attempt.retryable ? 'Retryable failure' : 'Failed';
    const isExpanded = this.expandedAttemptIds.has(attempt.id);

    return html`
      <section class="smtp-attempt" data-attempt-id=${attempt.id}>
        <button
          type="button"
          class="smtp-attempt-header"
          aria-expanded=${isExpanded ? 'true' : 'false'}
          @click=${() => this.toggleSmtpAttempt(attempt.id)}
        >
          <div class="smtp-attempt-heading">
            <div class="smtp-attempt-title-row">
              <svg
                class="smtp-attempt-chevron"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="m9 18 6-6-6-6"></path>
              </svg>
              <div class="smtp-attempt-title">Queue attempt ${attempt.queueAttempt} | mx ${mxPosition.index} of ${mxPosition.total}</div>
            </div>
            <div class="smtp-attempt-target">${destinationLabel}</div>
          </div>
          <div class="smtp-attempt-meta">
            <span class="smtp-attempt-badge ${badgeState}">${outcomeLabel}</span>
            <span>${this.formatSmtpDuration(attempt.durationMs)}</span>
            <span>started ${attempt.startedAt}</span>
            <span>completed ${attempt.completedAt}</span>
            ${attempt.smtpCode !== undefined ? html`<span>SMTP ${attempt.smtpCode}</span>` : ''}
            ${attempt.failurePhase ? html`<span>at ${this.getSmtpPhaseLabel(attempt.failurePhase)}</span>` : ''}
            ${attempt.errorType ? html`<span>${attempt.errorType}</span>` : ''}
          </div>
        </button>
        <div class="smtp-attempt-content" ?hidden=${!isExpanded}>
          <div class="smtp-attempt-transcript">
            ${phases.length > 0 ? phases.map((phase, phaseIndex) => html`
              <div class="smtp-phase-separator" data-phase=${phase.phase}>
                <div class="smtp-phase-line"></div>
                <span class="smtp-phase-label">${phase.label}</span>
                <div class="smtp-phase-line"></div>
              </div>
              ${phase.phase === 'message_body'
                ? this.renderMessageTransferSummary(
                    email,
                    phase.entries,
                    phases.slice(phaseIndex + 1).flatMap((followingPhase) => followingPhase.entries),
                  )
                : phase.entries.map((entry) => this.renderStructuredSmtpEntry(email, attempt, entry))}
            `) : html`<div class="smtp-empty">No protocol entries were captured for this attempt.</div>`}
          </div>
          ${attempt.error ? html`
            <div class="smtp-attempt-error">
              ${attempt.failurePhase ? `${this.getSmtpPhaseLabel(attempt.failurePhase)}: ` : ''}${attempt.error}
            </div>
          ` : ''}
        </div>
      </section>
    `;
  }

  private getPendingSmtpAttempt(email: IEmailDetail): {
    nextAttemptAt: number;
    nextAttemptNumber: number;
  } | undefined {
    const transactions = email.smtpTransactions || [];
    const latestAttempt = transactions.at(-1);
    if (
      transactions.length === 0
      || !latestAttempt
      || (email.status !== 'pending' && email.status !== 'deferred')
      || latestAttempt.outcome !== 'failed'
      || !latestAttempt.retryable
      || !this.isNonNegativeFinite(email.nextAttemptAt)
      || email.nextAttemptAt <= 0
    ) {
      return undefined;
    }

    const fallbackAttemptNumber = this.isNonNegativeInteger(latestAttempt.queueAttempt)
      ? latestAttempt.queueAttempt + 1
      : transactions.length + 1;
    const nextAttemptNumber = this.isNonNegativeInteger(email.nextAttemptNumber)
      && email.nextAttemptNumber > 0
      ? email.nextAttemptNumber
      : fallbackAttemptNumber;
    return {
      nextAttemptAt: email.nextAttemptAt,
      nextAttemptNumber,
    };
  }

  private formatCountdownDuration(remainingMs: number): string {
    const totalSeconds = Math.max(0, Math.ceil(remainingMs / 1000));
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return [
      ...(hours > 0 ? [`${hours}h`] : []),
      ...(minutes > 0 ? [`${minutes}m`] : []),
      ...((seconds > 0 || (hours === 0 && minutes === 0)) ? [`${seconds}s`] : []),
    ].join(' ');
  }

  private stopCountdownTimer(): void {
    if (this.countdownInterval === undefined) return;
    clearInterval(this.countdownInterval);
    this.countdownInterval = undefined;
  }

  private synchronizeCountdownTimer(): void {
    const pendingAttempt = this.email ? this.getPendingSmtpAttempt(this.email) : undefined;
    if (
      !this.isConnected
      || !pendingAttempt
      || pendingAttempt.nextAttemptAt <= Date.now()
    ) {
      this.stopCountdownTimer();
      return;
    }
    if (this.countdownInterval !== undefined) return;

    this.countdownInterval = setInterval(() => {
      const now = Date.now();
      this.countdownNowMs = now;
      const currentPendingAttempt = this.email
        ? this.getPendingSmtpAttempt(this.email)
        : undefined;
      if (!currentPendingAttempt || currentPendingAttempt.nextAttemptAt <= now) {
        this.stopCountdownTimer();
      }
    }, 1000);
  }

  private renderPendingSmtpAttempt(pendingAttempt: {
    nextAttemptAt: number;
    nextAttemptNumber: number;
  }): TemplateResult {
    const remainingMs = pendingAttempt.nextAttemptAt - this.countdownNowMs;
    const countdownText = remainingMs > 0
      ? `Next attempt in ${this.formatCountdownDuration(remainingMs)}`
      : 'Next attempt due now — waiting for the delivery worker';
    return html`
      <section
        class="smtp-attempt smtp-pending-attempt"
        data-attempt-id=${`pending-${pendingAttempt.nextAttemptNumber}`}
      >
        <div class="smtp-attempt-header smtp-pending-attempt-header">
          <div class="smtp-attempt-heading">
            <div class="smtp-attempt-title-row">
              <div class="smtp-attempt-title">
                Queue attempt ${pendingAttempt.nextAttemptNumber} — scheduled
              </div>
            </div>
          </div>
          <div class="smtp-attempt-meta">
            <span class="smtp-attempt-badge pending">Pending</span>
          </div>
        </div>
        <div class="smtp-pending-attempt-content">
          <span>${countdownText}</span>
          <span>·</span>
          <span class="smtp-pending-attempt-time">
            at ${new Date(pendingAttempt.nextAttemptAt).toLocaleString()}
          </span>
        </div>
      </section>
    `;
  }

  private renderSmtpLog(email: IEmailDetail): TemplateResult {
    const transactions = email.smtpTransactions || [];
    if (transactions.length > 0) {
      const pendingAttempt = this.getPendingSmtpAttempt(email);
      const logItems: Array<
        | {
            kind: 'attempt';
            key: string;
            attempt: ISmtpTransactionAttempt;
            mxPosition: { index: number; total: number };
          }
        | {
            kind: 'pending';
            key: string;
            pendingAttempt: { nextAttemptAt: number; nextAttemptNumber: number };
          }
      > = [
        ...transactions.map((attempt) => ({
          kind: 'attempt' as const,
          key: `attempt-${attempt.id}`,
          attempt,
          mxPosition: this.getMxPosition(transactions, attempt),
        })),
        ...(pendingAttempt ? [{
          kind: 'pending' as const,
          key: `pending-${pendingAttempt.nextAttemptNumber}`,
          pendingAttempt,
        }] : []),
      ];
      return html`
        <div class="smtp-log-container">
          ${directives.repeat(
            logItems,
            (item) => item.key,
            (item) => item.kind === 'attempt'
              ? this.renderSmtpTransaction(email, item.attempt, item.mxPosition)
              : this.renderPendingSmtpAttempt(item.pendingAttempt),
          )}
        </div>
      `;
    }

    const phases = this.getLegacySmtpPhases(email.smtpLog);

    return html`
      <div class="smtp-log-container">
        ${phases.length > 0 ? phases.map((phase) => html`
          <div class="smtp-phase-separator">
            <div class="smtp-phase-line"></div>
            <span class="smtp-phase-label">${phase.label}</span>
            <div class="smtp-phase-line"></div>
          </div>
          ${phase.entries.map((entry) => html`
            <div class="smtp-bubble ${entry.direction} ${
              this.getSmtpIdentityClass(email, entry.direction)
            }">
              ${entry.direction === 'server' && entry.responseCode !== undefined ? html`
                <span class="response-code-badge ${this.getResponseCodeBadgeClass(entry.responseCode)}">${entry.responseCode}</span>
              ` : ''}
              <div class="smtp-bubble-command">${this.redactSmtpText(entry.command, entry.direction)}</div>
              <div class="smtp-bubble-meta">
                <span>${entry.timestamp}</span>
                <span>·</span>
                <span class="smtp-direction-tag ${entry.direction}">${
                  this.getSmtpDirectionLabel(email, entry.direction)
                }</span>
              </div>
            </div>
          `)}
        `) : html`<div class="smtp-empty">No SMTP transaction has been captured for this email.</div>`}
      </div>
    `;
  }

  private formatSmtpLogForCopy(email: IEmailDetail): string {
    const transactions = email.smtpTransactions || [];
    if (transactions.length > 0) {
      const attemptsText = transactions.map((attempt) => {
        const outcome = attempt.outcome === 'succeeded'
          ? 'Succeeded'
          : attempt.retryable ? 'Retryable failure' : 'Failed';
        const metadata = [
          `SMTP transaction ${attempt.id}`,
          `Queue item: ${attempt.queueItemId}`,
          `Queue attempt: ${attempt.queueAttempt}`,
          `Target: ${attempt.targetHost}:${attempt.targetPort}`,
          ...(attempt.recipientDomain ? [`Recipient domain: ${attempt.recipientDomain}`] : []),
          ...(attempt.recipients.length > 0 ? [`Recipients: ${attempt.recipients.join(', ')}`] : []),
          `Started: ${attempt.startedAt}`,
          `Completed: ${attempt.completedAt}`,
          `Duration: ${this.formatSmtpDuration(attempt.durationMs)}`,
          `Outcome: ${outcome}`,
          ...(attempt.smtpCode !== undefined ? [`SMTP code: ${attempt.smtpCode}`] : []),
          ...(attempt.failurePhase ? [`Failure phase: ${this.getSmtpPhaseLabel(attempt.failurePhase)}`] : []),
          ...(attempt.errorType ? [`Error type: ${attempt.errorType}`] : []),
          ...(attempt.error ? [`Error: ${attempt.error}`] : []),
        ];
        const phases = this.getStructuredSmtpPhases(attempt.transcript || []);
        const transcript = phases.flatMap((phase, phaseIndex) => {
          if (phase.phase === 'message_body') {
            const timestamp = phase.entries
              .map((entry) => entry.timestampMs)
              .find((value) => this.isNonNegativeFinite(value));
            const labels = this.getMessageTransferMetricLabels(
              email,
              phase.entries,
              phases.slice(phaseIndex + 1).flatMap((followingPhase) => followingPhase.entries),
            );
            const summary = labels.length > 0
              ? `Message body transferred: ${labels.join(' · ')}`
              : 'Message body transferred';
            return [`[${this.formatSmtpTimestamp(timestamp ?? Number.NaN)}] SYS [message_body] ${summary}`];
          }
          return phase.entries.map((entry) => {
            const direction = entry.direction === 'client' ? 'C' : entry.direction === 'server' ? 'S' : 'SYS';
            const text = this.redactSmtpText(entry.text, entry.direction, entry.phase);
            return `[${this.formatSmtpTimestamp(entry.timestampMs)}] ${direction} [${entry.phase}] ${text}`;
          });
        });
        return [...metadata, '', ...transcript].join('\n');
      }).join('\n\n');
      const pendingAttempt = this.getPendingSmtpAttempt(email);
      return [
        attemptsText,
        ...(pendingAttempt ? [
          `Next attempt scheduled at ${new Date(pendingAttempt.nextAttemptAt).toLocaleString()} (queue attempt ${pendingAttempt.nextAttemptNumber})`,
        ] : []),
      ].join('\n\n');
    }

    return email.smtpLog
      .map((entry) => {
        const direction = entry.direction === 'client' ? 'C' : entry.direction === 'server' ? 'S' : 'SYS';
        return `[${entry.timestamp}] ${direction}: ${this.redactSmtpText(entry.command, entry.direction)}`;
      })
      .join('\n');
  }

  private copySmtpLog(): void {
    if (!this.email) return;
    const text = this.formatSmtpLogForCopy(this.email);
    navigator.clipboard.writeText(text);
  }

  private handleBack() {
    this.dispatchEvent(new CustomEvent('back', { bubbles: true, composed: true }));
  }
}
